import React, { Children } from 'react';
import { motion } from 'framer-motion';
function HeroBadge() {
  return <div className="relative w-[280px] h-[280px] md:w-[320px] md:h-[320px] mx-auto animate-pulse-glow" data-id="element-79">
      <svg viewBox="0 0 300 300" className="w-full h-full" aria-hidden="true" data-id="element-80">
        <defs data-id="element-81">
          <linearGradient id="coralGrad" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-82">
            <stop offset="0%" stopColor="#FF6B6B" data-id="element-83" />
            <stop offset="100%" stopColor="#FF8A5C" data-id="element-84" />
          </linearGradient>
          <linearGradient id="coralGradDark" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-85">
            <stop offset="0%" stopColor="#FF6B6B" stopOpacity="0.3" data-id="element-86" />
            <stop offset="100%" stopColor="#FF8A5C" stopOpacity="0.1" data-id="element-87" />
          </linearGradient>
          <filter id="innerGlow" data-id="element-88">
            <feGaussianBlur stdDeviation="4" result="blur" data-id="element-89" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-90" />
          </filter>
        </defs>
        {/* Outer hexagon */}
        <polygon points="150,15 270,80 270,220 150,285 30,220 30,80" fill="none" stroke="url(#coralGrad)" strokeWidth="2.5" opacity="0.8" data-id="element-91" />
        {/* Inner hexagon fill */}
        <polygon points="150,35 255,92 255,208 150,265 45,208 45,92" fill="url(#coralGradDark)" stroke="url(#coralGrad)" strokeWidth="1.5" data-id="element-92" />
        {/* Shield shape inside */}
        <path d="M150,70 L210,105 L210,190 C210,220 150,250 150,250 C150,250 90,220 90,190 L90,105 Z" fill="none" stroke="url(#coralGrad)" strokeWidth="2" opacity="0.6" data-id="element-93" />
        {/* Star motif */}
        <polygon points="150,100 162,135 200,135 170,158 180,193 150,172 120,193 130,158 100,135 138,135" fill="url(#coralGrad)" opacity="0.9" filter="url(#innerGlow)" data-id="element-94" />
        {/* Center circle */}
        <circle cx="150" cy="152" r="12" fill="#0A1628" stroke="url(#coralGrad)" strokeWidth="2" data-id="element-95" />
        {/* Small decorative lines */}
        <line x1="150" y1="30" x2="150" y2="50" stroke="#FF6B6B" strokeWidth="1" opacity="0.5" data-id="element-96" />
        <line x1="260" y1="86" x2="245" y2="96" stroke="#FF6B6B" strokeWidth="1" opacity="0.5" data-id="element-97" />
        <line x1="260" y1="214" x2="245" y2="204" stroke="#FF6B6B" strokeWidth="1" opacity="0.5" data-id="element-98" />
        <line x1="150" y1="270" x2="150" y2="250" stroke="#FF6B6B" strokeWidth="1" opacity="0.5" data-id="element-99" />
        <line x1="40" y1="214" x2="55" y2="204" stroke="#FF6B6B" strokeWidth="1" opacity="0.5" data-id="element-100" />
        <line x1="40" y1="86" x2="55" y2="96" stroke="#FF6B6B" strokeWidth="1" opacity="0.5" data-id="element-101" />
      </svg>
    </div>;
}
interface ParticleProps {
  index: number;
}
function Particle({
  index
}: ParticleProps) {
  const shapes = ['hexagon', 'diamond', 'circle'] as const;
  const shape = shapes[index % 3];
  const size = 6 + index % 4 * 3;
  const left = 5 + index * 37 % 90;
  const top = 5 + index * 23 % 90;
  const duration = 6 + index % 5 * 2;
  const delay = index % 7 * 1.2;
  const dx = -40 + index * 17 % 80;
  const dy = -60 + index * 13 % 40;
  const rot = index * 47 % 360;
  const opacity = 0.15 + index % 4 * 0.08;
  const isCoral = index % 3 !== 0;
  const style: React.CSSProperties = {
    left: `${left}%`,
    top: `${top}%`,
    '--duration': `${duration}s`,
    '--delay': `${delay}s`,
    '--dx': `${dx}px`,
    '--dy': `${dy}px`,
    '--rot': `${rot}deg`,
    '--particle-opacity': `${opacity}`
  } as React.CSSProperties;
  if (shape === 'circle') {
    return <div className="particle" style={style} data-id="element-102">
        <svg width={size} height={size} viewBox="0 0 10 10" aria-hidden="true" data-id="element-103">
          <circle cx="5" cy="5" r="4" fill={isCoral ? '#FF6B6B' : '#FF8A5C'} data-id="element-104" />
        </svg>
      </div>;
  }
  if (shape === 'diamond') {
    return <div className="particle" style={style} data-id="element-105">
        <svg width={size} height={size} viewBox="0 0 10 10" aria-hidden="true" data-id="element-106">
          <polygon points="5,0 10,5 5,10 0,5" fill={isCoral ? '#FF6B6B' : '#FF8A5C'} data-id="element-107" />
        </svg>
      </div>;
  }
  return <div className="particle" style={style} data-id="element-108">
      <svg width={size} height={size} viewBox="0 0 12 10" aria-hidden="true" data-id="element-109">
        <polygon points="6,0 12,3 12,7 6,10 0,7 0,3" fill={isCoral ? '#FF6B6B' : '#FF8A5C'} data-id="element-110" />
      </svg>
    </div>;
}
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const badgeVariants = {
  hidden: {
    opacity: 0,
    scale: 0.7
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 1,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export function Hero() {
  const particles = Array.from({
    length: 25
  }, (_, i) => i);
  return <section id="hero" className="relative min-h-screen w-full bg-navy flex items-center justify-center overflow-hidden" data-id="element-111">
      {/* Particle field */}
      <div className="absolute inset-0" aria-hidden="true" data-id="element-112">
        {particles.map(i => <Particle key={i} index={i} data-id="element-113" />)}
      </div>

      {/* Radial glow behind badge */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-20 pointer-events-none" style={{
      background: 'radial-gradient(circle, rgba(255,107,107,0.3) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-114" />

      <motion.div className="relative z-10 max-w-4xl mx-auto px-6 text-center" variants={containerVariants} initial="hidden" animate="visible" data-id="element-115">
        <motion.div variants={badgeVariants} className="mb-10" data-id="element-116">
          <HeroBadge data-id="element-117" />
        </motion.div>

        <motion.h1 variants={itemVariants} className="font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6 tracking-tight" data-id="element-118">
          Transform Player Retention
          <br data-id="element-119" />
          <span className="bg-gradient-to-r from-coral to-coral-light bg-clip-text text-transparent" data-id="element-120">
            Through AI-Driven Gamification
          </span>
        </motion.h1>

        <motion.p variants={itemVariants} className="font-body text-base sm:text-lg md:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-121">
          Viveludique's neural loyalty engine turns every player interaction
          into a personalized progression journey — boosting lifetime value and
          slashing churn for iGaming operators.
        </motion.p>

        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-122">
          <a href="#pricing" className="inline-flex items-center justify-center px-8 py-3.5 bg-gradient-to-r from-coral to-coral-light text-white font-body font-semibold rounded-full text-base transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,107,107,0.4)] hover:scale-105" data-id="element-123">
            Request a Demo
          </a>
          <a href="#solution" className="inline-flex items-center justify-center px-8 py-3.5 border-2 border-coral text-coral font-body font-semibold rounded-full text-base transition-all duration-300 hover:bg-coral hover:text-white hover:scale-105" data-id="element-124">
            See How It Works
          </a>
        </motion.div>
      </motion.div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-navy to-transparent pointer-events-none" aria-hidden="true" data-id="element-125" />
    </section>;
}